<template>
  <div class="grid grid-cols-2 sm-grid-cols-1 md-grid-cols-1 gap-x-lg gap-y-lg">
    <div role="form" :class="forAppEmbed ? 'p-x-none' : 'p-x-m'">
      <h1 class="m-t-m m-b-md font-heavy">Private Lesson Checkout</h1>
      <div v-if="error" class="alert alert-danger">
        {{ errorMessage }}
      </div>
      <div v-if="checkoutSuccess" class="alert alert-success">
        <i class="fa-solid fa-circle-check"></i> Lesson completed successfully. {{ processingMessage }}
      </div>

      <!-- Lesson Information -->
      <div>
        <div class="flex flex-wrap gap-x-xl gap-y-m">
          <div>
            <label class="font-semi m-y-none">Coach</label>
            <div class="flex align-items-center gap-x-sm m-t-xs">
              <i class="fa-solid fa-user text-gray-700"></i>
              <span>{{ coachNames }}</span>
            </div>
          </div>

          <div>
            <label class="font-semi m-y-none">Date & Time</label>
            <div class="flex align-items-center gap-x-sm m-t-xs">
              <i class="fa-solid fa-calendar-days text-gray-700"></i>
              <span>{{ eventTimeRange }}</span>
            </div>
          </div>

          <div v-if="event.location">
            <label class="font-semi m-y-none">Location</label>
            <div class="flex align-items-center gap-x-sm m-t-xs">
              <i class="fa-solid fa-location-dot text-gray-700"></i>
              <span>{{ event.location }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Private lesson notes, which will be sent through WIQ messages -->
      <div class="form-group flex flex-col m-t-m">
        <h2 class="font-heavy">Lesson Notes</h2>
        <label class="sr-only">Private lesson notes</label>
        <div class="bg-gray-50 p-xs border-l-4 border-solid border-gray-400 p-l-sm m-b-sm">
          <p class="text-gray-600 m-y-none"><i class="fa-solid fa-circle-info"></i> Notes are sent to the guardian billed for the lesson via a personal WrestlingIQ message between the coach who taught the lesson and them. Notes are also visible to all wrestlers in the lesson, and their guardians, if they view the lesson later.</p>
        </div>
        <div v-if="!(lessonAlreadyCompleted || checkoutSuccess)">
          <input id="x-editor" type="hidden" name="content">
          <trix-editor id="private-lesson-notes-form" input="x-editor" class="trix-content" :class="forAppEmbed ? 'font-no-ios-zoom' : ''"></trix-editor>
        </div>
        <div v-else>
          <div class="notepad-card flex w-full">
            <div class="flex-col flex m-l-m w-full">
              <div class="flex justify-space-between align-items-baseline">
                <h4 class="m-y-none"><span class="font-heavy">Notes from this lesson</span></h4>
              </div>
              <div class="m-t-sm trix-content" v-html="lessonNoteHtml"></div>
            </div>
          </div>

        </div>
      </div>

      <!-- Wrestlers and Payments -->
      <div v-if="paidEventBookings.length > 0">
        <h2 class="font-heavy m-t-lg">Previously Paid Wrestlers</h2>
        <div class="bg-gray-50 p-xs border-l-4 border-solid border-gray-400 p-l-sm m-b-sm">
          <p class="text-gray-600 m-y-none"><i class="fa-solid fa-circle-info"></i> These wrestlers have already paid for this lesson and will not be charged again.</p>
        </div>
        <div v-if="paidEventBookings.length > 0">
          <div v-for="booking in paidEventBookings" :key="booking.id" class="border border-solid border-green-600 rounded-xs p-m m-b-sm">
            <div class="flex flex-col-xs-only justify-space-between gap-xs">
              <div class="flex gap-m">
                <img :src="booking.profile.avatar" class="profile-pic" v-if="booking.profile.avatar" />
                <div v-else class="profile-pic" style="background: #E5E7EB;" aria-label="Wrestler avatar placeholder"></div>
                <a :href="`/wrestlers/${booking.profile.id}`">
                  <h3 class="m-b-none m-t-none">{{ booking.profile.display_name }}</h3>
                </a>
              </div>
              <div class="flex flex-col gap-xs">
                <div>
                  <span class="font-semi">{{ formatAsCurrency(booking.manual_invoice.amount_paid) }}</span> paid by <a :href="billableUrl(booking.manual_invoice.billing_profile.billable)">{{ booking.manual_invoice.billing_profile.billable.display_name }}</a> with {{ booking.manual_invoice.payment_method }} on {{ formatDate(booking.manual_invoice.paid_at) }}.
                </div>
                <div class="label label-primary bg-green-50 text-green-800 font-normal border border-solid border-green-500">
                  <i class="fa-solid fa-check"></i> Paid
                </div>
                <a v-if="booking.manual_invoice.receipt" class="btn btn-default" :href="booking.manual_invoice.receipt.cloudfront_url" target="_blank">View Receipt <i class="fa-solid fa-up-right-from-square m-l-xs"></i></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 class="font-heavy m-t-lg">Review Payments</h2>
      <div class="bg-gray-50 p-xs border-l-4 border-solid border-gray-400 p-l-sm m-b-sm">
        <p class="text-gray-600 m-y-none"><i class="fa-solid fa-circle-info"></i> Need to remove a wrestler from this lesson? Go back and mark their booking as canceled before checking out.</p>
      </div>
      <div v-for="booking in eventBookings" :key="booking.id" class="border border-solid border-gray-200 rounded-xs p-m m-b-sm">
        <div class="flex justify-space-between">
          <div class="flex gap-x-sm">
            <img :src="booking.profile.avatar" class="profile-pic" v-if="booking.profile.avatar" />
            <div v-else class="profile-pic" style="background: #E5E7EB;" aria-label="Wrestler avatar placeholder"></div>
            <h3 class="m-b-none m-t-none">{{ booking.profile.display_name }}</h3>
          </div>
        </div>

        <div class="m-t-m" v-if="booking.manual_invoice">
          <div class="grid grid-cols-2 sm-grid-cols-1 md-grid-cols-1 gap-lg">
            <div class="form-group">
                <label>Invoice Details</label>
                <p>{{ booking.manual_invoice.description }}</p>
                <div class="flex flex-col gap-xs">
                  <div v-for="item in invoiceItemsForDisplay(booking)" :key="`${booking.manual_invoice.id} - ${item.id}`" class="flex justify-space-between">
                    <span>{{ item.description }}</span>
                    <span>{{ formatAsCurrency(item.amount) }}</span>
                  </div>
                </div>
                <div class="flex justify-space-between align-items-center m-y-sm border-y border-solid border-gray-200 p-y-xs">
                  <span class="font-semi">Discount</span>
                  <div class="flex align-items-center">
                    <label class="font-normal sr-only">Discount amount</label>
                    <money
                      v-model="booking.discountInput"
                      v-bind="money"
                      class="form-control"
                      style="max-width: 100px; text-align: right;"
                      @focus.native="booking.discountFieldFocused = true"
                      @blur.native="updateInvoice(booking, { discountChanged: true })"
                      :disabled="booking.isUpdating"
                    ></money>
                    <Spinner v-if="booking.isUpdating" :inline="true" :ml="8" :useSvg="true"/>
                  </div>
                </div>
                <div class="flex justify-space-between m-t-sm">
                  <span class="font-semi">Subtotal</span>
                  <span>{{ formatAsCurrency(getSubtotalWithDiscount(booking)) }}</span>
                </div>
                <div v-if="getWiqFee(booking) > 0" class="flex justify-space-between m-t-sm">
                  <span>WrestlingIQ fee</span>
                  <span>{{ formatAsCurrency(getWiqFee(booking)) }}</span>
                </div>
                <div v-if="getTaxFee(booking) > 0" class="flex justify-space-between m-t-sm">
                  <span>Tax</span>
                  <span>{{ formatAsCurrency(getTaxFee(booking)) }}</span>
                </div>
                <div class="flex justify-space-between m-t-sm border-t border-solid border-gray-200 p-xs bg-gray-50 text-gray-900 rounded-b-xs">
                  <span class="font-semi ">Total</span>
                  <span class="font-semi">{{ formatAsCurrency(getTotal(booking)) }}</span>
                </div>
              </div>
            <div v-if="booking.manual_invoice.status === 'pending'">
              <div>
                <label>Billed To</label>
                <div class="flex flex-col gap-xs">{{ booking.manual_invoice.billing_profile.billable.display_name }}</div>
              </div>
              <label class="m-t-sm">Payment Method</label>
              <div class="m-t-xs">
                <label class="radio-inline">
                  <input
                    type="radio"
                    value="card"
                    id="none"
                    v-model="booking.manual_invoice.payment_method"
                    @change="updateInvoice(booking, { paymentMethodChanged: true })"
                    :disabled="booking.isUpdating"
                  > Charge card on file
                </label>
                <div v-if="booking.manual_invoice.billing_profile.default_payment_method_reusable" class="m-t-sm">
                  <div class="flex align-items-center gap-x-sm">
                    <div v-html="cardLogo(booking.manual_invoice.billing_profile.default_payment_method_brand)"></div>
                    <div class="font-heavy">* * * * {{
                        booking.manual_invoice.billing_profile.default_payment_method_last4
                      }}
                    </div>
                    <div class="">
                      {{ booking.manual_invoice.billing_profile.default_payment_method_exp_month }} / {{ booking.manual_invoice.billing_profile.default_payment_method_exp_year }}
                    </div>
                  </div>
                </div>
                <p v-else>Card on file has not been authorized for autocharging.</p>
              </div>
              <div class="m-t-m">
                <label class="radio-inline">
                  <input
                    type="radio"
                    value="cash"
                    id="all"
                    data-cy="pay-with-cash"
                    v-model="booking.manual_invoice.payment_method"
                    @change="updateInvoice(booking, { paymentMethodChanged: true })"
                    :disabled="booking.isUpdating"
                  > Cash
                </label>
              </div>
            </div>
            <div v-else-if="booking.manual_invoice.status === 'paid'" class="m-t-sm alert alert-success">
              <i class="fa-solid fa-circle-check"></i>
              Payment completed on {{ formatDate(booking.manual_invoice.paid_at) }}
            </div>
            <div v-else-if="booking.manual_invoice.status === 'overdue'" class="m-t-sm alert alert-danger">
              <i class="fa-solid fa-circle-exclamation"></i>
              Payment failed. The customer has been notified via email to update their card on file. WrestlingIQ will automatically attempt to charge the card on file again once it has been updated.
              You can also manually retry the charge below.
            </div>
            <div v-else>
              Invoice status: {{ booking.manual_invoice.status }}
            </div>
          </div>
        </div>

        <div v-else class="m-t-m alert alert-warning">
          <i class="fa-solid fa-circle-exclamation"></i> No invoice found for this booking.
        </div>
      </div>
      <ladda-button
        el-class="btn btn-primary m-t-lg w-full btn-block text-18"
        :loading="completingLesson || pollingActive"
        @lbClicked="completePrivateLesson"
        v-if="eventBookings.length > 0"
        data-cy="checkout-lesson"
        :disabled="hasPendingUpdates || checkoutSuccess || pollingActive"
        >
        Checkout
      </ladda-button>
      <p v-if="pollingActive" class="text-center">{{ processingMessage }}</p>
      <div v-if="bookingsAreUpdating" class="m-t-sm alert alert-warning">
        <i class="fa-solid fa-circle-exclamation"></i> Please wait for all updates to complete before checking out.
      </div>
      <div v-if="bookingsHaveDiscountFocused" class="m-t-sm alert alert-warning">
        <i class="fa-solid fa-circle-exclamation"></i> Please finalize your discounts before checking out (click or tap out of the field to save the discount).
      </div>
      <div v-if="eventBookings.length === 0 && !checkoutSuccess" class="m-t-sm alert alert-success">
        <i class="fa-solid fa-check"></i> All bookings have been paid.
      </div>
      <div v-if="!pollingActive && checkoutSuccess" class="m-t-sm alert" :class="eventBookings.length === 0 ? 'alert-success' : 'alert-danger'">
        <i class="fa-solid fa-check"></i> Lesson completed. <br><br>{{ processingMessage }}
      </div>
    </div>
    <div>
      <div :class="forAppEmbed ? 'bg-gray-800 text-gray-50 -m-x-lg p-x-lg p-b-lg' : 'bg-gray-800 rounded-sm p-x-xxs-only-xs p-x-lg p-y-m text-gray-50'">
        <div class="flex flex-col">
          <h1 class="m-t-m m-b-xs font-heavy text-gray-50">WrestlingIQ Private Lesson Checkout Overview</h1>
          <p class="">What happens after you press the checkout button?</p>
        </div>
        <div>
          <h2 class="m-t-lg m-b-xs font-heavy text-white bg-green-600 rounded-xxs p-x-xs p-y-xs w-fit">1. Payments are processed</h2>
          <p>
            We will charge the card on file and send receipts. If a card fails, WrestlingIQ will send an email asking for the recipient to update their card on file. Once updated, WrestlingIQ will automatically retry the charge and you can return here to retry the charge if needed.
          </p>
          <h2 class="m-t-lg m-b-xs font-heavy text-white bg-green-600 rounded-xxs p-x-xs p-y-xs w-fit">2. Lesson notes sent via WrestlingIQ message</h2>
          <p>
            Notes are sent out to guardians who were billed for the lesson via an individual WrestlingIQ message between them and the coach.
          </p>
          <div v-if="lessonAlreadyCompleted">
            <p>Lesson has already been completed, notes have been sent.</p>
          </div>
          <div v-else>
            <div class="trix-content" v-html="getRichNoteText()"></div>
          </div>
          <h2 class="m-t-lg m-b-xs font-heavy text-white bg-green-600 rounded-xxs p-x-xs p-y-xs w-fit">3. Lesson marked complete</h2>
          <p>
            Wrestlers and guardians will be able to view all previously completed lessons, as well as lesson notes, on the wrestler's profile page under the "Private Lessons" tab.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Money } from 'v-money';
import moment from 'moment';
import { formattingMixin } from './mixins/formatting_mix';
import { currencyMix } from '../../shared/currency_mix';
import { errorableMix } from './mixins/errorable_mix';
import LaddaButton from '../../shared/ladda_button.vue';
import { cardLogoMix } from '../../shared/card_logo_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import Spinner from './spinner.vue';

export default {
  name: 'private-lesson-checkout',
  mixins: [formattingMixin, currencyMix, errorableMix, cardLogoMix, appEmbedMix],
  components: {
    LaddaButton, Money, Spinner,
  },
  props: {
    privateLessonId: {
      type: Number,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    wiqFeePercent: {
      type: Number,
      default: 0,
    },
    taxRatePercent: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      completingLesson: false,
      checkoutSuccess: false,
      processingMessage: '',
      privateLesson: null,
      trixEditor: null,
      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false,
      },
      eventBookings: [],
      paidEventBookings: [],
      // Typed lesson notes only reach the server on completion, so they're the
      // work a native back-swipe would destroy (drives the exit guard).
      notesDirty: false,
      pollingActive: false,
      // The interval where we check for updates on the event bookings
      pollingInterval: null,
      pollingAttempts: 0,
    };
  },
  created() {
    this.initEventBookings(this.event.event_bookings);
  },
  mounted() {
    document.addEventListener('trix-initialize', this.trixInitHandler);
    document.addEventListener('trix-file-accept', this.rejectAttachments);
    document.addEventListener('trix-change', this.trixChangeHandler);
  },
  destroyed() {
    document.removeEventListener('trix-initialize', this.trixInitHandler);
    document.removeEventListener('trix-file-accept', this.rejectAttachments);
    document.removeEventListener('trix-change', this.trixChangeHandler);
    // Clear all polling intervals when component is destroyed
    this.clearAllPolling();
  },
  computed: {
    lessonNoteHtml() {
      return this.event.private_lessons.find((lesson) => lesson.status === 'completed').notes;
    },
    lessonAlreadyCompleted() {
      return this.event.private_lessons.some((lesson) => lesson.status === 'completed');
    },
    eventTimeRange() {
      if (!this.event.start_at || !this.event.end_at) return '';

      const start = moment(this.event.start_at);
      const end = moment(this.event.end_at);

      return `${start.format('ddd MMMM Do YYYY, h:mm a')} - ${end.format('h:mm a')}`;
    },
    coachBookings() {
      return this.event.event_bookings.filter((booking) => booking.profile_type === 'CoachProfile');
    },
    coachNames() {
      return this.coachBookings
        .map((booking) => booking.profile?.display_name)
        .join(', ');
    },
    today() {
      return moment().format('YYYY-MM-DD');
    },
    bookingsAreUpdating() {
      return this.eventBookings.some((booking) => booking.isUpdating);
    },
    bookingsHaveDiscountFocused() {
      return this.eventBookings.some((booking) => booking.discountFieldFocused);
    },
    hasPendingUpdates() {
      return this.bookingsAreUpdating || this.bookingsHaveDiscountFocused;
    },
    unpaidBookings() {
      const uncollectibleStates = ['paid', 'partially_paid', 'void', 'canceled'];
      return this.eventBookings.filter((booking) => booking.manual_invoice
        && !uncollectibleStates.includes(booking.manual_invoice.status));
    },
    eventBookingParamsForBilling() {
      return this.unpaidBookings.map((booking) => ({
        id: booking.id,
      }));
    },
    // Unsent notes or an in-flight completion/charge — the states where a
    // native back would lose work (or orphan the user mid-payment).
    exitGuardActive() {
      return this.completingLesson
        || this.pollingActive
        || (!this.checkoutSuccess && this.notesDirty);
    },
  },
  watch: {
    exitGuardActive(active) {
      this.setNativeExitConfirmation(active, 'Leave this checkout?');
    },
  },
  methods: {
    billableUrl(billable) {
      switch (billable.type) {
        case 'ParentProfile':
          return `/parents/${billable.id}`;
        case 'CoachProfile':
          return `/coaches/${billable.id}`;
        default:
          return null;
      }
    },
    initEventBookings(bookings) {
      this.eventBookings = [];
      this.paidEventBookings = [];
      bookings.forEach((booking) => {
        if (booking.profile_type === 'WrestlerProfile' && booking.status === 'confirmed') {
          // First if the booking is confirmed, but the manual_invoice is overdue we have a payment issue.
          // In that world we do not allow edits, but we do push it into the eventBookings array so it displays.
          if (booking.manual_invoice && booking.manual_invoice.status === 'overdue') {
            this.eventBookings.push(booking);
            return;
          }

          // Next if the booking is confirmed, we allow edits before attempting to process the card.
          // Find the discount item if it exists
          const discountItem = booking.manual_invoice.invoice_items.find((item) => item.description === 'Discount');
          // Initialize the discount input value - either from the existing discount item or zero
          booking.discountInput = discountItem ? Math.abs(discountItem.amount / 100) : 0;
          // Add a flag for tracking individual booking update status
          booking.isUpdating = false;
          booking.discountFieldFocused = false;
          // Initialize the debounced update function for this specific booking
          booking.debouncedUpdate = _.debounce(this.performUpdate.bind(this, booking), 500);
          this.eventBookings.push(booking);
        } else if (booking.profile_type === 'WrestlerProfile' && booking.status === 'completed') {
          this.paidEventBookings.push(booking);
        }
      });
    },
    // Methods for displaying invoice data
    invoiceItemsForDisplay(booking) {
      // Return regular invoice items (excluding fee, tax, and discount items which are shown separately)
      return booking.manual_invoice.invoice_items.filter((item) => item.description !== 'WrestlingIQ fee'
        && item.description !== 'Tax'
        && item.description !== 'Discount');
    },
    getDiscountItem(booking) {
      return booking.manual_invoice.invoice_items.find((item) => item.description === 'Discount');
    },
    getWiqFeeItem(booking) {
      return booking.manual_invoice.invoice_items.find((item) => item.description === 'WrestlingIQ fee');
    },
    getTaxItem(booking) {
      return booking.manual_invoice.invoice_items.find((item) => item.description === 'Tax');
    },
    getSubtotalWithDiscount(booking) {
      // Base items total
      const baseTotal = _.sumBy(this.invoiceItemsForDisplay(booking), 'amount');

      // Add discount if present
      const discountItem = this.getDiscountItem(booking);
      return baseTotal + (discountItem ? discountItem.amount : 0);
    },
    getWiqFee(booking) {
      const feeItem = this.getWiqFeeItem(booking);
      return feeItem ? feeItem.amount : 0;
    },
    getTaxFee(booking) {
      const taxItem = this.getTaxItem(booking);
      return taxItem ? taxItem.amount : 0;
    },
    getTotal(booking) {
      return _.sumBy(booking.manual_invoice.invoice_items, 'amount');
    },
    updateInvoice(booking, options = {}) {
      // Set updating flag immediately for UI feedback
      booking.isUpdating = true;
      booking.discountFieldFocused = false;
      // Build a complete params object with all current values
      const params = {
        manual_invoice: {},
      };

      // Always include current values in the update
      if (options.discountChanged) {
        params.manual_invoice.new_discount_amount = Math.round(booking.discountInput * 100);
      }

      if (options.paymentMethodChanged) {
        params.manual_invoice.new_payment_method = booking.manual_invoice.payment_method;
      }

      // Trigger the booking-specific debounced update with the latest params
      booking.debouncedUpdate(params);
    },

    performUpdate(booking, params) {
      const url = `/api/v1/manual_invoices/${booking.manual_invoice.id}/update_for_private_lesson`;

      axios.put(url, params)
        .then((response) => {
          // Update the invoice items with the new data from the server
          booking.manual_invoice.invoice_items = response.data.invoice_items;
          booking.isUpdating = false;
        })
        .catch((error) => {
          booking.isUpdating = false;
          const errorMsg = this.parseErrorResponse(error);
          this.showError(errorMsg);
        });
    },

    cardLogo(brand) {
      switch (brand) {
        case 'visa':
          return this.visaLogo;
        case 'mastercard':
          return this.mastercardLogo;
        case 'amex':
        case 'american_express':
          return this.amexLogo;
        case 'discover':
          return this.discoverLogo;
        default:
          return `<div>${brand}</div>`;
      }
    },
    trixInitHandler(event) {
      const vm = this;
      const fileTools = document.querySelector('.trix-button-group--file-tools');
      if (fileTools) {
        fileTools.remove();
      }
      vm.trixEditor = event.target.editor;
    },
    trixChangeHandler() {
      // An empty trix document still stringifies to a newline, hence the trim
      this.notesDirty = !!this.trixEditor && this.trixEditor.getDocument().toString().trim().length > 0;
    },
    rejectAttachments(e) {
      // For now reject file attahcments on the private lesson checkout page.
      // We will likely allow attachments in the future, but maybe in the form of a video file and I don't want to deal with the complexity of handling that yet.
      e.preventDefault();
      e.stopPropagation();
    },
    getRichNoteText() {
      if (this.trixEditor) {
        return this.trixEditor.element.value;
      }

      return '';
    },
    getPlainNoteText() {
      if (this.trixEditor) {
        return this.trixEditor.getDocument().toString();
      }
      return '';
    },
    statusClass(status) {
      switch (status) {
        case 'confirmed':
          return 'label-primary';
        case 'completed':
          return 'label-success';
        case 'cancelled':
          return 'label-danger';
        case 'pending':
        case 'invited':
          return 'label-warning';
        default:
          return 'label-default';
      }
    },

    invoiceStatusClass(status) {
      switch (status) {
        case 'paid':
          return 'text-success';
        case 'pending':
        case 'overdue':
          return 'text-warning';
        case 'void':
          return 'text-danger';
        default:
          return '';
      }
    },

    formatDate(dateString) {
      return moment(dateString).format('MMMM D, YYYY');
    },

    completePrivateLesson: _.throttle(function () {
      const vm = this;
      if (vm.completingLesson) {
        return;
      }

      vm.error = false;
      vm.completingLesson = true;
      const richNote = vm.getRichNoteText();
      const params = {
        private_lesson: {
          id: vm.privateLessonId,
          notes: richNote,
          plain_notes: vm.getPlainNoteText(),
          event_bookings: vm.eventBookingParamsForBilling,
        },
      };
      const url = vm.$apiService.checkoutPrivateLessonUrl(vm.privateLessonId);
      axios.post(url, params)
        .then((response) => {
          vm.completingLesson = false;
          // Update the lesson status to completed and set the notes
          const lesson = vm.event.private_lessons.find((lesson) => lesson.id === vm.privateLessonId);
          this.$set(lesson, 'notes', richNote);
          this.$set(lesson, 'status', 'completed');
          vm.checkoutSuccess = true;

          vm.processingMessage = 'Processing payments, please wait...';
          vm.$notificationManager.emit('show-toast', 'Lesson completed, processing payments...', true);

          // Start polling for event booking updates using the event object
          vm.startPollingEvent();
        })
        .catch((error) => {
          vm.completingLesson = false;
          const errorMsg = vm.parseErrorResponse(error);
          vm.showError(errorMsg);
        });
    }, 500),

    // New event-based polling method
    startPollingEvent() {
      // Set initial state
      this.pollingActive = true;
      this.pollingAttempts = 0;

      // Poll every 1.5 seconds for updated event data
      this.pollingInterval = setInterval(() => {
        this.pollEventStatus();
      }, 1500);
    },

    async pollEventStatus() {
      // Increment attempt counter
      this.pollingAttempts += 1;

      if (this.pollingAttempts > 4) {
        this.processingMessage = 'Still waiting on payments to process, please wait...';
      }

      try {
        // Fetch updated event with bookings
        const response = await axios.get(`/api/v1/events/${this.event.id}/bookings`);
        // The challenge now is that we need to know whether a booking was updated. Maybe we can keep track of the
        const oldUnpaidBookingsIdUpdatedAt = this.unpaidBookings.map((booking) => ({
          id: booking.id,
          updated_at: booking.updated_at,
        }));
        // Reboot the vue component, this is the same as refreshing the page.
        this.initEventBookings(response.data.event_bookings);

        if (this.unpaidBookings.length === 0) {
          this.processingMessage = 'All payments have been processed and paid successfully.';
          if (this.forAppEmbed) {
            this.askAppToPop({
              name: 'event_booking_paid',
              data: {
                alert: {
                  type: 'success',
                  message: 'All payments have been processed and paid successfully.',
                },
              },
            });
          }
          this.finishPolling();
        } else {
          // In this if block it is a partially failed lesson.
          // Check to make sure that all the unpaid bookings have been updated (we might have multiple failed charges) based on the updated_at timestamp
          const needsToPoll = oldUnpaidBookingsIdUpdatedAt.some((oldBooking) => {
            const newBooking = this.unpaidBookings.find((b) => b.id === oldBooking.id);
            // Continue polling if booking hasn't been updated and is still in the unpaid list
            return newBooking && new Date(newBooking.updated_at) <= new Date(oldBooking.updated_at);
          });
          if (!needsToPoll) {
            this.processingMessage = 'We have attempted to charge for the lesson, but one or more payment failed. We have sent out an email to all guardians with a failed payment. Ask them to click the button in the email to update their card, we will retry as soon as the card is updated. Review the list above for more details.';
            this.finishPolling();
          }
        }

        // Stop polling if all processed or too many attempts
        if (this.pollingAttempts >= 20) {
          this.processingMessage = 'Payment status check timed out. Refresh the page to view payment status.';
          this.finishPolling();
        }
      } catch (error) {
        console.error('Error polling event status:', error);
        // After multiple errors, give up
        if (this.pollingAttempts >= 20) {
          this.finishPolling();
          this.processingMessage = 'Payment status check timed out. Refresh the page to view payment status.';
        }
      }
    },

    finishPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.pollingActive = false;
      this.pollingAttempts = 0;
    },

    clearAllPolling() {
      // Clear all active polling intervals
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.pollingActive = false;
      this.pollingAttempts = 0;
    },
  },
};
</script>
